import tokens from '@kluein/design-tokens';
import { Box, Button, Tip } from '@kluein/klue-ui';
import { NectarIcon } from '@kluein/nectar';
import { PlusCircle, SidebarSimple, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import TEST_IDS from 'test-ids';

type StyledHeaderProps = {
  isPage?: boolean;
};

const StyledHeader = styled(Box)<StyledHeaderProps>`
  padding: ${tokens.spacing.medium} ${tokens.spacing.xlarge};
  border-bottom: ${({ isPage }) => (isPage ? 'none' : 'solid 1px #e5e5e5')};
`;

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <Button
      variant="plain"
      size="medium"
      onClick={onClose}
      data-test-id={TEST_IDS.askKlue.answerModal.closeButton}
      aria-label="Close"
      icon={<X size={tokens.spacing.large} color={tokens.color.neutral[900]} />}
    />
  );
}

function AskKlueModalHeader({
  onClose,
  toggleSidebar,
  handleNewChat,
  isResponseLoading = false,
  isPage = false,
}: {
  onClose?: () => void;
  toggleSidebar: () => void;
  handleNewChat?: () => void;
  isResponseLoading?: boolean;
  isPage?: boolean;
}) {
  const { t } = useTranslation(['Dashboard']);

  return (
    <StyledHeader
      direction="row"
      justify="between"
      align="center"
      id="ask-klue-modal-header"
      isPage={isPage}
    >
      <Box direction="row" justify="between" align="center" width="full">
        <Box direction="row" gap="xxsmall" align="center">
          <Tip
            content={t('Dashboard:askKlue.answerModal.chatHistoryButton')}
            dropProps={{ align: { bottom: 'top' } }}
            color="dark"
            variant="small"
          >
            <Box>
              <Button
                variant="plain"
                size="medium"
                onClick={toggleSidebar}
                data-test-id={TEST_IDS.askKlue.answerModal.sidebarButton}
                icon={
                  <SidebarSimple
                    size={tokens.spacing.large}
                    color={tokens.color.neutral[900]}
                  />
                }
              />
            </Box>
          </Tip>
          <Tip
            content={t('Dashboard:askKlue.answerModal.newChatButton')}
            dropProps={{ align: { bottom: 'top' } }}
            color="dark"
            variant="small"
          >
            <Box>
              <Button
                variant="plain"
                size="medium"
                onClick={handleNewChat}
                data-test-id={TEST_IDS.askKlue.answerModal.newChatButton}
                disabled={isResponseLoading}
                icon={
                  <PlusCircle
                    size={tokens.spacing.large}
                    color={tokens.color.neutral[900]}
                  />
                }
              />
            </Box>
          </Tip>
        </Box>
        {!isPage && (
          <Box direction="row" gap="xxsmall" align="center">
            <NectarIcon.KlueLogo
              color={tokens.color.neutral[1000]}
              weight="regular"
              size={tokens.iconSize.large}
            />
            <NectarIcon.KlueAI
              color={tokens.color.neutral[1000]}
              weight="regular"
              size={tokens.iconSize.medium}
            />
          </Box>
        )}
        {/* Right side content */}
        {onClose ? (
          <>
            <Box direction="row" align="center" gap="small">
              {onClose && <CloseButton onClose={onClose} />}
            </Box>
            {isPage ? <div /> : null}
          </>
        ) : (
          <div /> // empty div ensures the header components are aligned correctly.
        )}
      </Box>
    </StyledHeader>
  );
}

export default AskKlueModalHeader;
